import React from "react";
import { Layout, Layouts } from "react-grid-layout";
import { AvailableWidget, WidgetConfig } from "./types";
import { GridTemplateConfig } from "./templates";

/** Mora pratiti `cols` u Grid.tsx — po tome se računaju širine i minimumi. */
export const GRID_COLS: Record<string, number> = {
  lg: 12,
  md: 10,
  sm: 6,
  xs: 4,
  xxs: 2,
};

/**
 * Ispod velikog ekrana predložak se napušta: mreža ide na dva pa na jedan stupac.
 * Trećina od 996px je uža od bilo koje tablice u widgetu.
 */
const STACKED_WIDTHS: Record<string, number> = {
  md: 5,
  sm: 6,
  xs: 4,
  xxs: 2,
};

const DEFAULT_MIN_W = 3;
const DEFAULT_MIN_H = 4;

/** Minimum ne smije biti širi od same mreže — na xxs su svega dva stupca. */
const clampMinW = (minW: number, cols: number): number =>
  Math.max(1, Math.min(minW, cols));

/**
 * Slaže widgete slijeva nadesno, u novi red kad ne stanu.
 *
 * `keepWidth` čuva zadanu širinu i kad je uža od widgetova minimuma — na velikom
 * ekranu vrijedi predložak koji je korisnik odabrao u "Prikaz", pa red mora ostati
 * onakav kakav je na sličici. Minimum ondje služi samo kao donja granica povlačenja.
 */
const buildLayout = (
  widgetsData: AvailableWidget[],
  cols: number,
  getWidth: (index: number) => number,
  rowHeight: number,
  keepWidth = false
): Layout[] => {
  const placed: {
    index: number;
    x: number;
    row: number;
    w: number;
    minW: number;
    minH: number;
  }[] = [];

  let x = 0;
  let row = 0;

  widgetsData.forEach((widgetData, index) => {
    const width = Math.min(cols, getWidth(index));
    const minW = keepWidth
      ? Math.min(clampMinW(widgetData.minW ?? DEFAULT_MIN_W, cols), width)
      : clampMinW(widgetData.minW ?? DEFAULT_MIN_W, cols);
    const w = Math.max(width, minW);

    if (x + w > cols) {
      x = 0;
      row += 1;
    }

    placed.push({
      index,
      x,
      row,
      w,
      minW,
      minH: widgetData.minH ?? DEFAULT_MIN_H,
    });

    x += w;
  });

  // Svi widgeti u istom redu dobivaju istu visinu — najvišeg u redu. Inače se donji
  // rubovi razilaze i sljedeći red kreće na različitim visinama po stupcima.
  const rowHeights: number[] = [];
  placed.forEach((item) => {
    rowHeights[item.row] = Math.max(rowHeights[item.row] ?? rowHeight, item.minH);
  });

  const rowOffsets: number[] = [];
  let y = 0;
  rowHeights.forEach((height, index) => {
    rowOffsets[index] = y;
    y += height;
  });

  return placed.map((item) => ({
    i: `widget-${item.index}`,
    x: item.x,
    y: rowOffsets[item.row],
    w: item.w,
    h: rowHeights[item.row],
    minW: item.minW,
    minH: item.minH,
    maxW: cols,
  }));
};

export const generateGrid = (
  rows: number,
  cols: number,
  w = 4,
  h = 6,
  widgetsData?: AvailableWidget[]
): { widgets: WidgetConfig[]; layouts: Layouts } => {
  const widgets: WidgetConfig[] = [];
  const layouts: Layouts = { lg: [] };

  let idCounter = 0;

  for (let i = 0; i < rows * cols; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;

    const widgetData = widgetsData?.[i];
    const id = `widget-${idCounter}`;
    const hasContent = Boolean(widgetData);

    const title = hasContent ? widgetData!.name : `Prazno ${i + 1}`;
    const icon = hasContent ? widgetData!.icon : "";

    const content = hasContent ? (
      widgetData!.component
    ) : (
      <div className="flex items-center justify-center h-full text-gray-400">
        Prazno {i + 1}
      </div>
    );

    const header = hasContent ? (
      widgetData!.header
    ) : (
      <></>
    );

    const minW = clampMinW(widgetData?.minW ?? DEFAULT_MIN_W, cols * w);
    const minH = widgetData?.minH ?? DEFAULT_MIN_H;
    const widgetWidth = Math.max(w, minW);
    const widgetHeight = Math.max(h, minH);

    widgets.push({
      i: id,
      title,
      header,
      icon,
      content,
      isActive: hasContent,
      w: widgetWidth,
      h: widgetHeight,
      minW,
      minH,
    });

    layouts.lg.push({
      i: id,
      x: col * w,
      y: row * h,
      w: widgetWidth,
      h: widgetHeight,
      minW,
      minH,
      maxW: cols * w,
    });

    idCounter++;
  }

  return { widgets, layouts };
};

export const generateGridFromTemplate = (
  template: GridTemplateConfig,
  widgetsData: AvailableWidget[]
): { widgets: WidgetConfig[]; layouts: Layouts } => {
  const colsPerRow = template.widths.length;

  // Predložak vrijedi na velikom ekranu; uži breakpointi ga svode na stupce koji stanu.
  const lg = buildLayout(
    widgetsData,
    GRID_COLS.lg,
    (index) => template.widths[index % colsPerRow],
    template.rowHeight,
    true
  );

  const layouts: Layouts = {
    lg,
    md: buildLayout(widgetsData, GRID_COLS.md, () => STACKED_WIDTHS.md, template.rowHeight),
    sm: buildLayout(widgetsData, GRID_COLS.sm, () => STACKED_WIDTHS.sm, template.rowHeight),
    xs: buildLayout(widgetsData, GRID_COLS.xs, () => STACKED_WIDTHS.xs, template.rowHeight),
    xxs: buildLayout(widgetsData, GRID_COLS.xxs, () => STACKED_WIDTHS.xxs, template.rowHeight),
  };

  const widgets: WidgetConfig[] = widgetsData.map((widgetData, index) => {
    const item = lg[index];

    return {
      i: item.i,
      title: widgetData.name,
      header: widgetData.header,
      icon: widgetData.icon,
      content: widgetData.component,
      isActive: true,
      w: item.w,
      h: item.h,
      minW: item.minW,
      minH: item.minH,
    };
  });

  return { widgets, layouts };
};

/**
 * Spremljeni raspored iz localStorage-a nastao je prije minimuma, pa zna imati
 * kartice manje od svog sadržaja. Slaganje se zadržava, a premale se podignu.
 */
export const clampLayoutsToWidgetMinimums = (
  layouts: Layouts,
  widgets: WidgetConfig[]
): Layouts => {
  const byId = new Map(widgets.map((widget) => [widget.i, widget]));
  const clamped: Layouts = {};

  Object.entries(layouts ?? {}).forEach(([breakpoint, items]) => {
    const cols = GRID_COLS[breakpoint] ?? GRID_COLS.lg;

    clamped[breakpoint] = (items ?? []).map((item) => {
      const widget = byId.get(item.i);
      if (!widget) return item;

      const minW = clampMinW(widget.minW ?? DEFAULT_MIN_W, cols);
      const minH = widget.minH ?? DEFAULT_MIN_H;

      return {
        ...item,
        w: Math.min(cols, Math.max(item.w, minW)),
        h: Math.max(item.h, minH),
        minW,
        minH,
      };
    });
  });

  return clamped;
};
